/* The conflict dialog's entire appearance.
 *
 * `<CSPProvider disableStyleElements>` means Base UI ships no CSS whatsoever, so nothing here is an
 * override of a library default — there are no library defaults. Every rule is the first and only
 * declaration of that property for these parts, including the ones that would normally be free:
 * without `.backdrop`'s fill the page behind stays fully lit, and without `.popup`'s `position` the
 * dialog renders in the document flow at the bottom of the page.
 *
 * No canon file draws a builder dialog, so it is composed from the tokens the canon's other floating
 * surfaces use — the same set `AccountMenu.module.css` settled on: `--bg-card` over
 * `--shadow-card-lift`, a hairline border, the `--r-control` radius.
 *
 * NOT `--shadow-pop`: on the Flame themes that token is a COLOURED blur, which is the glow the
 * handoff bans outright. It exists for the mono theme, where it is neutral.
 *
 * The ACCENT is absent from this dialog on purpose. Adopting the server's tree discards the
 * trainer's work, so the button that does it is `--danger`, not ember — the accent marks the
 * trainer's own action, and this is the one that undoes it. */

.backdrop {
  position: fixed;
  inset: 0;
  z-index: 30;
  background: var(--scrim);
  transition: opacity var(--dur-fast) var(--ease);
}

.backdrop[data-starting-style],
.backdrop[data-ending-style] {
  opacity: 0;
}

/* Centred by translation rather than by flexbox on the backdrop: the backdrop and the popup are
 * siblings under the portal, not parent and child, so there is no box to centre within.
 *
 * Bounded for the reason `ConfirmDialog` records at length — it is the same object and wears the
 * same surface. This one starts closer to the bound than the confirm does: the changes list below is
 * capped at 240px on its own, so a conflict naming eight edits is already a ~400px card before the
 * description wraps. The two scrollers nest, and only the inner one engages at any viewport this app
 * supports. */
.popup {
  position: fixed;
  top: 50%;
  left: 50%;
  z-index: 31;
  width: min(520px, calc(100vw - var(--space-8)));
  max-height: var(--overlay-max-block);
  padding: var(--space-5);
  overflow-y: auto;
  overscroll-behavior: contain;
  background: var(--bg-card);
  border: var(--border-hairline) solid var(--border);
  border-radius: var(--r-control);
  outline: none;
  box-shadow: var(--shadow-card-lift);
  transform: translate(-50%, -50%);
  transition: opacity var(--dur-fast) var(--ease), transform var(--dur-fast) var(--ease);
}

/* An opacity fade and 4px of rise. No scale and no bounce — `tokens/base.css` collapses every
 * duration in the app under `prefers-reduced-motion`, so there is no media query here (and a feature
 * stylesheet is forbidden one). */
.popup[data-starting-style],
.popup[data-ending-style] {
  opacity: 0;
  transform: translate(-50%, calc(-50% + var(--space-1)));
}

.title {
  margin-bottom: var(--space-2);
  font-size: var(--fs-h3);
  font-weight: var(--fw-extrabold);
  letter-spacing: var(--ls-heading);
  color: var(--text);
}

.description {
  margin-bottom: var(--space-3);
  font-size: var(--fs-body);
  color: var(--text-2);
}

/* The list scrolls rather than growing the dialog past the viewport. A concurrent edit can name
 * eight changes, and eight lines of Polish wrap further on a narrow window than they do here. */
.changes {
  max-height: 240px;
  padding: var(--space-3);
  overflow-y: auto;
  background: var(--bg-inset);
  border-radius: var(--radius-sm);
}

.change {
  font-size: var(--fs-label);
  color: var(--text);
}

.change + .change {
  margin-top: var(--space-2);
}

.overflow {
  margin-top: var(--space-2);
  font-size: var(--fs-caption);
  color: var(--text-2);
}

.actions {
  display: flex;
  gap: var(--space-2);
  justify-content: flex-end;
  margin-top: var(--space-4);
}

.ghost,
.danger {
  display: inline-flex;
  align-items: center;
  height: var(--h-control);
  padding: var(--space-0) var(--space-14px);
  font-size: var(--fs-label);
  font-weight: var(--fw-bold);
  letter-spacing: var(--ls-heading);
  cursor: pointer;
  border-radius: var(--r-button);
}

.ghost {
  color: var(--text);
  background: transparent;
  border: var(--border-hairline) solid var(--border-2);
}

.ghost:hover {
  background: var(--bg-inset);
}

/* A flat fill, no glow.
 *
 * ── White on `--danger` failed, and the fix is the INK, not a different red ──────────────────
 * The pairing here was `--on-accent` (white in every theme) on `--danger` (#ff5a4d in every theme),
 * described as "the same one the app's destructive controls already use". It measures **3.08:1** —
 * below AA for a `--fw-bold` label at `--fs-label`, on the one button in this app that discards the
 * trainer's unsaved work. The button whose consequences are worst was the hardest to read.
 *
 * `--on-danger` is #111113 on the same fill: **6.13:1**, in both themes.
 *
 * Two alternatives were measured and rejected. Filling with `--danger-text-strong` and keeping white
 * gives 6.65:1 on LIGHT but collapses in dark, where that token is deliberately the bright red again
 * (#ff5a4d) and the pair returns to 3.08. Demoting the red to a hairline with a `--text` label is
 * theme-safe but leaves this button and the ghost beside it as two outlined buttons of the same
 * weight, which removes the only signal saying which of the two is destructive.
 *
 * Using `--danger` as a FILL is inside its stated contract — "non-text use only — dots, fills,
 * borders, rings". The contract forbids drawing WORDS in that red, and nothing here does. */
.danger {
  color: var(--on-danger);
  background: var(--danger);

  /* Three parts rather than a shorthand — the value allowlist requires a token colour in a `border`,
   * and this edge exists only to match the ghost button's height beside it. */
  border-width: var(--border-hairline);
  border-style: solid;
  border-color: transparent;
}

/* The hover LIFTS rather than darkens, and that follows from the label being ink.
 *
 * It used to darken the fill to `--danger-ink`. With white text that raised contrast; with ink text
 * it lowers it — #111113 on #d6453c is 4.29:1, so the button would have passed at rest and failed
 * the moment the pointer was on it, which is the state a trainer reads it in. Darkening is simply
 * the wrong direction under a dark label.
 *
 * `--shadow-hover` is a NEUTRAL shadow in both Flame themes, which is what the handoff allows beside
 * a flat fill; it is not a coloured blur, so the no-glow rule holds. The fill stays put, so the
 * measured 6.13:1 is the contrast in every state. */
.danger:hover {
  box-shadow: var(--shadow-hover);
}

.ghost:focus-visible,
.danger:focus-visible {
  outline: var(--border-focus) solid var(--accent);
  outline-offset: var(--space-2px);
}
